import { useState, type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { DenseTable, type Column } from "../../components/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { useInteractionsPaged } from "./data.ts";
import { MetricTiles, ThreatPill } from "./components.tsx";
import { DetailDrawer } from "./DetailDrawer.tsx";
import { InteractionDetail } from "./InteractionDetail.tsx";
import type { Interaction } from "./types.ts";
import "./agentdna.css";

/**
 * Interactions (AgentDNA port): the ledger of every signed, recorded cross-actor exchange — one
 * agent calling another agent or a tool, within an intent. Each row carries the initiator, target,
 * the owning intent, runtime and a threat flag. Clicking a row's Details opens a right-side drawer
 * with the full interaction (flow, metadata, audit trail, raw record), mirroring the AgentDNA
 * dashboard. Viewer-level; the server is the real authority.
 */
export function InteractionsScreen(): ReactNode {
  const { role } = useIdentity();
  const { data, loading } = useInteractionsPaged(1);
  const [selected, setSelected] = useState<Interaction | null>(null);

  const state = resolveState(role, loading, data.items.length);
  const titleId = "interaction-detail-title";

  const columns: readonly Column<Interaction>[] = [
    { key: "initiator", header: "Initiator", isRowHeader: true, cell: (r) => r.initiator.name },
    { key: "target", header: "Target", cell: (r) => r.target.name },
    { key: "intent", header: "Intent", cell: (r) => r.intent.name },
    { key: "runtime", header: "Runtime", align: "right", cell: (r) => `${r.runtime} ms` },
    { key: "threat", header: "Threat", cell: (r) => <ThreatPill threat={r.threat} /> },
    {
      key: "created",
      header: "Time",
      align: "right",
      cell: (r) => <time dateTime={isoAttr(r.created)}>{formatDateTime(r.created)}</time>,
    },
  ];

  return (
    <ScreenScaffold
      title="Interactions"
      subtitle="Every cross-actor exchange, signed and recorded."
      state={state}
    >
      <MetricTiles
        tiles={[
          { label: "Interactions", value: data.total },
          { label: "Threats", value: data.items.filter((i) => i.threat).length },
        ]}
      />
      <DenseTable
        caption="Interactions ledger"
        columns={columns}
        rows={data.items}
        rowKey={(r) => r.id}
        emptyMessage="No interactions recorded yet."
        onRowClick={(r) => setSelected(r)}
        rowLabel={(r) => `View details for the interaction from ${r.initiator.name} to ${r.target.name}`}
      />
      <DetailDrawer open={selected !== null} onClose={() => setSelected(null)} labelledBy={titleId}>
        {selected !== null && (
          <InteractionDetail interaction={selected} onClose={() => setSelected(null)} titleId={titleId} />
        )}
      </DetailDrawer>
    </ScreenScaffold>
  );
}

function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  loading: boolean,
  count: number,
): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet. The server enforces access regardless."
          : "Interactions need at least the viewer role.",
    };
  }
  if (loading) return { kind: "loading" };
  if (count === 0) {
    return { kind: "empty", cause: "No interactions have been recorded for this organization yet." };
  }
  return { kind: "ready" };
}
